import React, { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Trash2, Mail, ArrowLeft, Link as LinkIcon } from 'lucide-react';
import { workspacesAPI } from '../services/api';
import Button from './common/Button';
import Alert from './common/Alert';
import LoadingSpinner from './common/LoadingSpinner';

const Workspaces = () => {
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [selected, setSelected] = useState(null); // detailed workspace
  const [links, setLinks] = useState([]);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [info, setInfo] = useState('');

  const load = useCallback(async () => {
    const r = await workspacesAPI.list();
    if (r.success) setList(r.data.workspaces);
    else setError(r.message || 'Failed to load workspaces');
  }, []);

  useEffect(() => { load(); }, [load]);

  const openWorkspace = async (id) => {
    setInfo('');
    const [ws, lk] = await Promise.all([workspacesAPI.get(id), workspacesAPI.links(id)]);
    if (ws.success) setSelected(ws.data);
    if (lk.success) setLinks(lk.data.links);
  };

  const create = async () => {
    if (!name.trim()) return;
    const r = await workspacesAPI.create({ name });
    if (r.success) { setName(''); load(); }
    else setError(r.message || 'Failed to create workspace');
  };

  const invite = async () => {
    const r = await workspacesAPI.invite(selected.id, { email: inviteEmail, role: inviteRole });
    if (r.success) { setInfo(`Invitation sent to ${inviteEmail}.`); setInviteEmail(''); }
    else setError(r.message || 'Failed to invite');
  };

  const removeMember = async (userId) => {
    await workspacesAPI.removeMember(selected.id, userId);
    openWorkspace(selected.id);
  };

  const removeWorkspace = async (id) => {
    if (!window.confirm('Delete this workspace?')) return;
    const r = await workspacesAPI.remove(id);
    if (r.success) { setSelected(null); load(); }
    else setError(r.message || 'Only the owner can delete a workspace');
  };

  // Detail view
  if (selected) {
    const canManage = ['owner', 'admin'].includes(selected.my_role);
    return (
      <div className="max-w-4xl mx-auto">
        <button onClick={() => setSelected(null)} className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-500 hover:text-ink-700">
          <ArrowLeft className="h-4 w-4" /> All workspaces
        </button>
        <header className="mt-4 flex items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink-900">{selected.name}</h1>
            <p className="mt-1 text-sm text-ink-500">Your role: {selected.my_role}</p>
          </div>
          {selected.my_role === 'owner' && (
            <Button variant="danger" size="small" onClick={() => removeWorkspace(selected.id)}>
              <Trash2 className="h-4 w-4" /> Delete
            </Button>
          )}
        </header>

        {info && <Alert type="success" message={info} className="mt-5" onClose={() => setInfo('')} />}
        {error && <Alert type="error" message={error} className="mt-5" onClose={() => setError('')} />}

        <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
            <h2 className="text-sm font-semibold text-ink-900 mb-4">Members ({selected.members.length})</h2>
            <ul className="space-y-2">
              {selected.members.map((m) => (
                <li key={m.id} className="flex items-center justify-between text-sm">
                  <span><span className="font-medium text-ink-900">{m.username}</span> <span className="text-ink-500">· {m.role}</span></span>
                  {canManage && m.role !== 'owner' && (
                    <button onClick={() => removeMember(m.user_id)} className="text-rose-600 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button>
                  )}
                </li>
              ))}
            </ul>
            {canManage && (
              <div className="mt-5 border-t border-ink-100 pt-4 space-y-2">
                <label className="block text-xs font-medium text-ink-600">Invite by email</label>
                <div className="flex gap-2">
                  <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@example.com"
                    className="flex-1 rounded-lg border border-ink-200 px-3 py-2 text-sm focus:outline-none focus:ring-4 focus:ring-brand-100" />
                  <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)} className="rounded-lg border border-ink-200 px-2 py-2 text-sm">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <Button size="small" onClick={invite} disabled={!inviteEmail}><Mail className="h-4 w-4" /></Button>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-2xl border border-ink-200 bg-white p-6 shadow-soft">
            <h2 className="text-sm font-semibold text-ink-900 mb-4">Shared links ({links.length})</h2>
            {links.length === 0 ? (
              <p className="text-sm text-ink-500">No links in this workspace yet. Assign a link to this workspace when creating it.</p>
            ) : (
              <ul className="space-y-2">
                {links.map((l) => (
                  <li key={l.id} className="flex items-center gap-2 text-sm">
                    <LinkIcon className="h-3.5 w-3.5 text-brand-500 shrink-0" />
                    <span className="font-mono text-ink-700 truncate">/r/{l.short_code}</span>
                    <span className="text-ink-400 truncate">— {l.title || l.original_url}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-4xl mx-auto">
      <header className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">Workspaces</h1>
        <p className="mt-1.5 text-sm text-ink-500">Collaborate on links with your team.</p>
      </header>

      {error && <Alert type="error" message={error} className="mb-5" onClose={() => setError('')} />}

      <div className="mb-6 flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New workspace name"
          className="flex-1 rounded-lg border border-ink-200 px-3 py-2.5 text-sm focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500" />
        <Button onClick={create} disabled={!name.trim()}><Plus className="h-4 w-4" /> Create</Button>
      </div>

      {list === null ? (
        <LoadingSpinner text="Loading workspaces…" />
      ) : list.length === 0 ? (
        <div className="text-center py-12 rounded-2xl border border-ink-200 bg-white">
          <Users className="mx-auto h-10 w-10 text-ink-300" />
          <p className="mt-3 text-sm text-ink-500">No workspaces yet. Create one to collaborate.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map((w) => (
            <button key={w.id} onClick={() => openWorkspace(w.id)}
              className="text-left rounded-2xl border border-ink-200 bg-white p-5 shadow-soft hover:shadow-pop hover:-translate-y-0.5 transition">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-ink-900">{w.name}</h3>
                <span className="text-xs rounded-full bg-brand-50 text-brand-600 px-2 py-0.5">{w.role}</span>
              </div>
              <p className="mt-2 text-sm text-ink-500">{w.member_count} member{w.member_count === 1 ? '' : 's'} · {w.link_count} link{w.link_count === 1 ? '' : 's'}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Workspaces;
